import * as React from 'react';

import { axios, useRefreshToken, useUserInfo } from 'src/core/api';

import { AuthorizationContext } from './AuthorizationContext';
import { UserInfo } from 'src/core/types/UserInfo';
import { deleteCookie } from './functions';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';

export interface AuthorizationProps {
  children?: React.ReactNode;
}

function Authorization(props: AuthorizationProps) {
  const { children } = props;

  const [accessToken, setAccessToken] = React.useState<string | null>(null);
  const [, setRefreshToken] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  const { mutate: mutateToken } = useRefreshToken();
  const { refetch: fetchUserInfo } = useUserInfo();

  /**
   * Called whenever a new access token is obtained. Updates the state and axios headers.
   *
   * @param {string} accessToken - The access token
   */
  const handleAccessToken = React.useCallback((accessToken: string | null) => {
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    setAccessToken(accessToken);
  }, []);

  /**
   * Sets the access token and refresh token to the states.
   *
   * @param {string} accessToken - Access token.
   * @param {string} refreshToken - Refresh token.
   * @returns {void}
   */
  const login = React.useCallback(
    (accessToken: string, refreshToken: string) => {
      localStorage.setItem('access', accessToken);
      localStorage.setItem('refresh', refreshToken);
      handleAccessToken(accessToken);
      setRefreshToken(refreshToken);
      navigate('/meetings/upcoming');
    },
    [handleAccessToken, navigate],
  );

  /**
   * Resets the states when user logs out.
   *
   * @returns{void}
   */
  const logout = React.useCallback(() => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');

    deleteCookie('refresh');
    deleteCookie('access');

    handleAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  }, [handleAccessToken]);

  React.useEffect(() => {
    setIsLoading(true);

    mutateToken(
      {},
      {
        onSuccess: (data) => {
          handleAccessToken(data.access);
        },
        onError: () => {
          // TODO:
        },
        onSettled: () => {
          setIsLoading(false);
        },
      },
    );
  }, [handleAccessToken, mutateToken]);

  React.useEffect(() => {
    if (!accessToken) {
      return;
    }

    fetchUserInfo().then(({ data }) => {
      if (data) {
        setUser(data);
      }
    });
  }, [accessToken, fetchUserInfo]);

  React.useEffect(() => {
    const unuthorizedInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response &&
          error.response.status === 401 /* Unauthorized Error */
        ) {
          if (accessToken) {
            logout();
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(unuthorizedInterceptor);
    };
  }, [accessToken, location, logout]);

  return (
    <AuthorizationContext.Provider
      value={{
        accessToken,
        login,
        logout,
        setUser,
        user,
      }}
    >
      {isLoading ? null : children}
    </AuthorizationContext.Provider>
  );
}

export default Authorization;
